import { COLORS } from '@/constants/COLORS';
import React, { ReactNode, useMemo, useRef, useEffect, useState } from 'react';

interface ProgressProps {
  progress: number;
  width: number;
  height: number;
  strokeWidth?: number;
  strokeColor?: string;
  backgroundColor?: string;
  borderRadius?: string;
}

const TierLevelSvg: React.FC<ProgressProps> = ({
  progress,
  width,
  height,
  strokeWidth = 4,
  strokeColor = COLORS.primaryViolet,
  backgroundColor = COLORS.violetLight,
  borderRadius = '1rem',
}) => {
  const pathRef = useRef<SVGPathElement>(null);
  const [pathLength, setPathLength] = useState(0);
  const innerWidth = width - strokeWidth;
  const innerHeight = height - strokeWidth;
  const radiusInPx = Math.min(parseFloat(borderRadius) * 16, innerHeight / 2, innerWidth / 2);

  const pathD = useMemo(() => {
    const topRight = [innerWidth + strokeWidth / 2 - radiusInPx, strokeWidth / 2];
    const bottomRight = [innerWidth + strokeWidth / 2, innerHeight + strokeWidth / 2 - radiusInPx];
    const bottomLeft = [strokeWidth / 2 + radiusInPx, innerHeight + strokeWidth / 2];
    const topLeft = [strokeWidth / 2, strokeWidth / 2 + radiusInPx];

    return `
      M ${width / 2} ${strokeWidth / 2}
      L ${topRight[0]} ${topRight[1]}
      Q ${innerWidth + strokeWidth / 2} ${strokeWidth / 2} ${innerWidth + strokeWidth / 2} ${strokeWidth / 2 + radiusInPx}
      L ${bottomRight[0]} ${bottomRight[1]}
      Q ${innerWidth + strokeWidth / 2} ${innerHeight + strokeWidth / 2} ${innerWidth + strokeWidth / 2 - radiusInPx} ${innerHeight + strokeWidth / 2}
      L ${bottomLeft[0]} ${bottomLeft[1]}
      Q ${strokeWidth / 2} ${innerHeight + strokeWidth / 2} ${strokeWidth / 2} ${innerHeight + strokeWidth / 2 - radiusInPx}
      L ${topLeft[0]} ${topLeft[1]}
      Q ${strokeWidth / 2} ${strokeWidth / 2} ${strokeWidth / 2 + radiusInPx} ${strokeWidth / 2}
      L ${width / 2} ${strokeWidth / 2}
    `;
  }, [width, height, strokeWidth, radiusInPx, innerWidth, innerHeight]);

  useEffect(() => {
    if (pathRef.current) {
      const length = pathRef.current.getTotalLength();
      setPathLength(length);
      pathRef.current.style.strokeDasharray = `${length} ${length}`;
      pathRef.current.style.strokeDashoffset = `${length}`;
    }
  }, [pathD]);

  const progressStyle = useMemo(
    () => ({
      strokeDashoffset: pathLength - (progress / 100) * pathLength,
      // transition: 'stroke-dashoffset 0.5s ease' // TODO : TEST MOVEMENT
    }),
    [pathLength, progress],
  );

  return (
    <svg width={width} height={height}>
      <path d={pathD} fill="none" stroke={backgroundColor} strokeWidth={strokeWidth} />
      <path
        ref={pathRef}
        d={pathD}
        fill="none"
        stroke={strokeColor}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
        style={progressStyle}
      />
    </svg>
  );
};

export default TierLevelSvg;
